import { useEffect, useState } from 'react';
import {
	Alert,
	Text,
	Stack,
	Grid,
	VStack,
	GridItem,
	RadioCard,
	DialogSteps,
} from '@welbe/design-system';
import { FaRegAddressCard } from 'react-icons/fa';
import { LuCake, LuMail, LuPhone } from 'react-icons/lu';
import { LiaTransgenderSolid } from 'react-icons/lia';
import { Footer } from './Footer';
import { SectionIconText } from './SectionIconText';
import type {
	EmployeeProps,
	MergedStepComponentProps,
} from '../MergeEmployeesDialog.types';
import { getEmployee } from '../../Employee/Employee.services';
import { Auth } from 'aws-amplify';

type ProfileSelectionProps = MergedStepComponentProps & {
	employees: EmployeeProps[];
};

const alert = {
	info: {
		title: 'Sobre el perfil principal',
		description:
			'El perfil seleccionado será el que conservará la la información personal del colaborador.',
	},
	error: {
		title: 'Usted no tiene permisos para unir este colaborador',
		description:
			'Solo personas con permisos de administrador pueden unir colaboradores.',
	},
};

export const ProfileSelection = ({
	primaryEmployeeId,
	employees,
	onClose,
	onNext,
	onBack,
}: ProfileSelectionProps) => {
	const [loading, setLoading] = useState<boolean>(true);
	const [patientsLocked, setPatientsLocked] = useState<boolean>(false);
	const [selectedId, setSelectedId] = useState<string>(primaryEmployeeId || '');

	const fetchEmployees = async () => {
		setLoading(true);
		const [firstEmployeeId, secondEmployeeId] = employees.map(
			(employee) => employee.id,
		);
		try {
			const { idToken }: any = await Auth.currentSession();
			const permissions = idToken?.payload?.permissions
				? JSON.parse(idToken.payload.permissions)
				: [];

			if (permissions.includes('company.employees.profile-edition')) {
				setPatientsLocked(false);
				return;
			}

			const res = await Promise.allSettled([
				getEmployee(firstEmployeeId),
				getEmployee(secondEmployeeId),
			]);
			const isFulfilled = res.every((result) => result.status === 'fulfilled');

			if (isFulfilled) {
				const [employeeA, employeeB] = res.map((result: any) => result.value);

				const employeeObjects = [
					employeeA.data.content,
					employeeB.data.content,
				];
				setPatientsLocked(
					employeeObjects.some((item) => item.isPatientDataLocked),
				);
			}
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchEmployees();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<DialogSteps.Body>
				<Stack gap="1rem" pt="1rem">
					<Stack>
						<Text fontSize="sm" color="#64778F">
							El expediente unificado se guardará con la siguiente información
							del titular.
						</Text>
						<Text fontSize="sm" color="#64778F">
							El perfil anterior quedará inactivo en el sistema, pero se
							mantendrá visible y no se podrán generar nuevas informaciones
							médicas asociadas con él
						</Text>
					</Stack>
					<Alert.Root status={patientsLocked ? 'error' : 'info'}>
						<Alert.Indicator />
						<Alert.Content>
							<Alert.Title fontWeight="bold">
								{patientsLocked ? alert.error.title : alert.info.title}
							</Alert.Title>
							<Alert.Description>
								{patientsLocked
									? alert.error.description
									: alert.info.description}
							</Alert.Description>
						</Alert.Content>
					</Alert.Root>
					<Stack>
						<RadioCard.Root
							value={selectedId}
							disabled={patientsLocked}
							onValueChange={(details: any) => setSelectedId(details.value)}
						>
							<VStack align="stretch" gap="2rem">
								{employees.map((item) => (
									<RadioCard.Item
										borderRadius="lg"
										key={item.id}
										value={item.id}
										maxW="396px"
										w="100%"
									>
										<RadioCard.ItemHiddenInput />
										<RadioCard.ItemControl>
											<RadioCard.ItemIndicator />
											<RadioCard.ItemText fontWeight="600" fontSize="sm">
												{item.name}
											</RadioCard.ItemText>
										</RadioCard.ItemControl>
										<Grid
											templateColumns="1fr 1fr"
											gap="0.5rem"
											p="0 1rem 1rem 3rem"
										>
											<GridItem colSpan={2}>
												<SectionIconText
													icon={<FaRegAddressCard />}
													label={item.nationalIdentity || '-'}
												/>
											</GridItem>
											<SectionIconText
												icon={<LuCake />}
												label={item.birthDate || '-'}
											/>

											<SectionIconText
												icon={<LiaTransgenderSolid />}
												label={item.gender || '-'}
											/>

											<SectionIconText
												icon={<LuMail />}
												label={item.email || item.alternativeEmail || '-'}
											/>

											<SectionIconText
												icon={<LuPhone />}
												label={item.phone || item.alternativePhone || '-'}
											/>
										</Grid>
									</RadioCard.Item>
								))}
							</VStack>
						</RadioCard.Root>
					</Stack>
				</Stack>
			</DialogSteps.Body>
			<Footer
				onClose={onClose}
				onBack={onBack}
				onNext={() => onNext(selectedId)}
				loading={loading}
				nextDisabled={!selectedId || loading}
			/>
		</>
	);
};
